/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  createContactGroup,
  deleteContactGroupById,
  getAllContactGroups,
  updateContactGroupById,
} from '@services/api/contact-group.api'
import {
  IContact,
  IContactGroup,
  IContactGroupParticipant,
} from '@dataTypes/contact.type'
import { bulkCreateContactGroupParticipant } from '@services/api/contact-group-participant.api'
import { RootState } from '@store/index'

export const fetchAllContactGroups = createAsyncThunk<
  IContactGroup[],
  void,
  { rejectValue: string }
>('api/contact-group/fetch/all', async (_, { rejectWithValue }) => {
  try {
    const response = await getAllContactGroups()
    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }

    if (
      response.data &&
      'contact_groups' in response.data &&
      Array.isArray(response.data.contact_groups)
    ) {
      return response.data.contact_groups as IContactGroup[]
    }

    return rejectWithValue('Contact groups not found in the response')
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

export const addContactGroup = createAsyncThunk<
  void,
  { group: IContactGroup; members?: IContact[] },
  {
    rejectValue: string
  }
>(
  'api/contact-group/add',
  async (data, { dispatch, getState, rejectWithValue }) => {
    const state = getState() as RootState
    const clinicId = state.clinic.selectedClinic?.clinic_id as string
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      const response = await createContactGroup({
        ...data.group,
        clinic_id: clinicId,
        created_by: user.user_id,
        updated_by: user.user_id,
      })

      if (response.statusCode !== 201) {
        return rejectWithValue(
          response.message || 'Failed to add contact group',
        )
      }

      if (
        'contact_group' in response.data &&
        data.members &&
        data.members.length
      ) {
        const groupData = response.data.contact_group as IContactGroup

        const membersData: IContactGroupParticipant[] = data.members
          .filter((member) => member.contact_id && groupData.contact_group_id)
          .map((member) => ({
            contact_group_id: groupData.contact_group_id as string,
            contact_id: member.contact_id as string,
            created_by: user.user_id,
            updated_by: user.user_id,
          }))

        const membersResponse =
          await bulkCreateContactGroupParticipant(membersData)

        if (membersResponse.statusCode !== 201) {
          return rejectWithValue(
            response.message || 'Failed to add contact group members',
          )
        }
      }
      await dispatch(fetchAllContactGroups()).unwrap()
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create contact group')
    }
  },
)

export const updateContactGroup = createAsyncThunk<
  IContactGroup,
  { contact_group_id: string; updates: Partial<IContactGroup> },
  { rejectValue: string }
>(
  'api/contact-group/update',
  async ({ contact_group_id, updates }, { rejectWithValue }) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      const response = await updateContactGroupById(contact_group_id, {
        ...updates,
        updated_by: user.user_id,
      })

      if (response.statusCode !== 200) {
        return rejectWithValue(response.message)
      }

      if (
        response.data &&
        'contact_group' in response.data &&
        typeof response.data.contact_group === 'object' &&
        !Array.isArray(response.data.contact_group)
      ) {
        return response.data.contact_group as IContactGroup
      }

      return rejectWithValue('Contact group not found in the response')
    } catch (error: any) {
      return rejectWithValue(error.message || 'Error occurred during update')
    }
  },
)

export const deleteContactGroup = createAsyncThunk<
  string,
  string,
  {
    rejectValue: string
  }
>('api/contact-group/delete', async (id, { rejectWithValue }) => {
  try {
    const response = await deleteContactGroupById(id)
    if (response.statusCode !== 200) {
      throw new Error(response.message)
    }
    return id
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})
